"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface SearchableSelectorOption {
	value: string;
	label: string;
}

interface SearchableSelectorProps {
	options: SearchableSelectorOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	emptyMessage?: string;
	className?: string;
	disabled?: boolean;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	onFocus?: () => void;
}

/**
 * A searchable selector component that allows the user to select an option from a list of options.
 * @param options - The options to select from.
 * @param value - The value of the selected option.
 * @param onChange - The function to call when the value changes.
 * @param placeholder - The placeholder text to display when no option is selected.
 * @param searchPlaceholder - The placeholder text to display when searching for an option.
 * @param emptyMessage - The message to display when no option is found.
 * @param className - The class name to apply to the component.
 * @param disabled - Whether the component is disabled.
 * @param open - Whether the component is open.
 * @param onOpenChange - The function to call when the open state changes.
 * @param onFocus - The function to call when the component is focused.
 */
export function SearchableSelector({
	options,
	value,
	onChange,
	placeholder = "Wähle eine Option...",
	searchPlaceholder = "Suche...",
	emptyMessage = "Keine Option gefunden.",
	className,
	disabled = false,
	open: externalOpen,
	onOpenChange: externalOnOpenChange,
	onFocus,
}: SearchableSelectorProps) {
	const [internalOpen, setInternalOpen] = React.useState(false);

	// Use external open state if provided, otherwise use internal state
	const open = externalOpen !== undefined ? externalOpen : internalOpen;
	const setOpen = externalOnOpenChange || setInternalOpen;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					aria-expanded={open}
					className={cn("w-full justify-between", className)}
					disabled={disabled}
					onFocus={onFocus}
				>
					{value
						? options.find((option) => option.value === value)?.label
						: placeholder}
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[--radix-popover-trigger-width] p-0"
				align="start"
				style={{ zIndex: 150 }}
				onWheel={(e) => e.stopPropagation()}
				onTouchMove={(e) => e.stopPropagation()}
			>
				<Command>
					<CommandInput placeholder={searchPlaceholder} className="h-9 pl-2" />
					<CommandList className="max-h-[300px] overflow-auto">
						<CommandEmpty>{emptyMessage}</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.value}
									value={`${option.label} ${option.value}`}
									onSelect={(currentValue) => {
										onChange(currentValue === value ? "" : option.value);
										setOpen(false);
									}}
								>
									{option.label}
									<Check
										className={cn(
											"ml-auto h-4 w-4",
											value === option.value ? "opacity-100" : "opacity-0",
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
